import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Button, Form, ProgressBar, Spinner } from 'react-bootstrap';
import {
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Circle,
  GripVertical,
  List,
  Plus,
  Trash,
  X
} from 'react-bootstrap-icons';

function CourseTrainingEditorSidebar({
  isLargeViewport,
  displayCourseTitle,
  editMode,
  completedCount,
  totalLessons,
  progressPercentage,
  modules,
  expandedModules,
  toggleModule,
  currentLesson,
  onSelectLesson,
  completedLessons,
  editingModuleId,
  setEditingModuleId,
  updateModuleTitle,
  onRequestDeleteModule,
  onRequestDeleteLesson,
  addLesson,
  addModule,
  addingModule = false,
  addingLessonModuleId = '',
  reorderingStructure = false,
  onStructureDragEnd,
  onExitEditor
}) {
  const structureLocked = addingModule || Boolean(addingLessonModuleId) || reorderingStructure;
  const [outlineOpen, setOutlineOpen] = useState(false);
  const mobileLearner = !isLargeViewport && !editMode;
  const showInlineOutline = isLargeViewport || (outlineOpen && !mobileLearner);

  const selectLessonFromList = (lessonId, event) => {
    // A touch can synthesize a second click after the outline collapses and the
    // lesson (often a video) has slid up under the finger. That click reloads
    // or fullscreens the media. preventDefault on touchend cancels it.
    if (event && event.type === 'touchend') event.preventDefault();
    onSelectLesson(lessonId);
    if (!isLargeViewport) setOutlineOpen(false);
  };

  const renderLessonRow = (module, lesson, lessonIndex, dragProvided, dragSnapshot) => {
    const moduleId = String(module._id ?? module.id ?? '');
    const lessonId = String(lesson._id ?? lesson.id ?? '');
    const isCurrent =
      currentLesson && String(currentLesson?._id ?? currentLesson?.id ?? '') === lessonId;
    const lessonBtnStyle = {
      appearance: 'none',
      WebkitAppearance: 'none',
      width: editMode ? 'auto' : '100%',
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      padding: editMode ? '10px 8px 10px 0.5rem' : '10px 8px 10px 2.75rem',
      textAlign: 'left',
      fontSize: '0.875rem',
      lineHeight: 1.35,
      border: 'none',
      borderTop: 'none',
      borderBottom: 'none',
      borderRight: 'none',
      borderLeft: '3px solid',
      borderRadius: 0,
      boxShadow: 'none',
      outline: 'none',
      cursor: 'pointer',
      boxSizing: 'border-box',
      transition: 'background-color 0.12s ease',
      borderLeftColor: isCurrent ? 'currentColor' : 'transparent',
      backgroundColor: dragSnapshot.isDragging
        ? '#f1f3f5'
        : isCurrent
        ? 'rgba(0, 0, 0, 0.05)'
        : 'transparent',
      fontWeight: isCurrent ? 600 : 400,
      WebkitTapHighlightColor: 'transparent',
      ...(isCurrent ? {} : { color: '#6c757d' })
    };

    return (
      <div
        ref={dragProvided.innerRef}
        {...dragProvided.draggableProps}
        className="d-flex align-items-stretch pr-2"
        style={{
          ...dragProvided.draggableProps.style,
          backgroundColor: dragSnapshot.isDragging ? '#f8f9fa' : undefined
        }}
      >
        {editMode ? (
          <span
            {...dragProvided.dragHandleProps}
            className="d-flex align-items-center flex-shrink-0 text-muted pl-4 pr-1"
            style={{ cursor: structureLocked ? 'not-allowed' : 'grab', touchAction: 'none' }}
            aria-hidden
          >
            <GripVertical size={14} />
          </span>
        ) : null}
        <button
          type="button"
          className={isCurrent ? 'text-primary' : ''}
          style={lessonBtnStyle}
          onClick={event => selectLessonFromList(lessonId, event)}
          onTouchEnd={event => selectLessonFromList(lessonId, event)}
          onMouseDown={e => e.preventDefault()}
          onMouseEnter={e => {
            if (!isCurrent && !dragSnapshot.isDragging) {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.035)';
            }
          }}
          onMouseLeave={e => {
            if (!dragSnapshot.isDragging) {
              e.currentTarget.style.backgroundColor = isCurrent ? 'rgba(0, 0, 0, 0.05)' : 'transparent';
            }
          }}
        >
          {!editMode ? (
            completedLessons.includes(lessonId) ? (
              <CheckCircle className="text-success flex-shrink-0 mr-2" size={16} />
            ) : (
              <Circle className="text-muted flex-shrink-0 mr-2" size={16} />
            )
          ) : null}
          <span style={{ color: 'inherit' }} className="text-truncate">
            {lesson.title || 'Untitled lesson'}
          </span>
        </button>
        {editMode && module.lessons.length > 1 ? (
          <Button
            variant="link"
            className="p-1 text-danger align-self-center flex-shrink-0"
            title="Remove lesson"
            disabled={structureLocked}
            onClick={() =>
              onRequestDeleteLesson(moduleId, lessonId, lesson.title?.trim() || 'Untitled lesson')
            }
          >
            <Trash size={14} />
          </Button>
        ) : null}
      </div>
    );
  };

  const renderModuleBlock = (module, moduleIndex, modProvided, modSnapshot) => {
    const moduleId = String(module._id ?? module.id ?? '');
    const isExpanded = expandedModules.includes(moduleId);

    return (
      <div
        ref={modProvided.innerRef}
        {...modProvided.draggableProps}
        key={moduleId}
        style={{
          borderBottom: '1px solid #e9ecef',
          borderTop: 'none',
          backgroundColor: modSnapshot.isDragging ? '#f8f9fa' : undefined,
          ...modProvided.draggableProps.style
        }}
      >
        <div className="d-flex align-items-center px-2" style={{ gap: 4 }}>
          {editMode ? (
            <>
              {modules.length > 1 ? (
                <span
                  {...modProvided.dragHandleProps}
                  className="d-flex align-items-center flex-shrink-0 text-muted pl-1"
                  style={{ cursor: structureLocked ? 'not-allowed' : 'grab', touchAction: 'none' }}
                  aria-hidden
                >
                  <GripVertical size={14} />
                </span>
              ) : null}
              <button
                type="button"
                aria-label={isExpanded ? 'Collapse module' : 'Expand module'}
                aria-expanded={isExpanded}
                className="d-flex align-items-center justify-content-center flex-shrink-0 text-dark"
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  padding: '12px 2px 12px 4px',
                  border: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
                  outline: 'none',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onClick={() => toggleModule(moduleId)}
                onMouseDown={e => e.preventDefault()}
              >
                {isExpanded ? (
                  <ChevronDown className="text-muted flex-shrink-0" size={16} />
                ) : (
                  <ChevronRight className="text-muted flex-shrink-0" size={16} />
                )}
              </button>
              <div className="flex-grow-1 min-w-0 py-2 pr-1" style={{ minWidth: 0 }}>
                {editingModuleId === moduleId ? (
                  <Form.Control
                    size="sm"
                    className="font-weight-medium"
                    value={module.title}
                    onChange={e => updateModuleTitle(moduleId, e.target.value)}
                    onBlur={() => setEditingModuleId(null)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setEditingModuleId(null);
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <button
                    type="button"
                    className="w-100 text-left border-0 bg-transparent p-0 font-weight-medium text-dark text-truncate"
                    style={{
                      fontSize: '0.875rem',
                      lineHeight: 1.35,
                      cursor: 'text',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    onClick={() => setEditingModuleId(moduleId)}
                    title="Click to edit module name"
                  >
                    {module.title || 'Untitled module'}
                  </button>
                )}
              </div>
              {modules.length > 1 ? (
                <Button
                  variant="link"
                  className="p-1 text-danger flex-shrink-0"
                  title="Remove module"
                  disabled={structureLocked}
                  onClick={() =>
                    onRequestDeleteModule(moduleId, module.title?.trim() || 'Untitled module')
                  }
                >
                  <Trash size={14} />
                </Button>
              ) : null}
            </>
          ) : (
            <button
              type="button"
              className="d-flex align-items-center justify-content-between text-dark flex-grow-1"
              style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                minWidth: 0,
                padding: '12px 8px 12px 8px',
                textAlign: 'left',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: 'none',
                borderRadius: 0,
                boxShadow: 'none',
                outline: 'none',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                boxSizing: 'border-box',
                WebkitTapHighlightColor: 'transparent'
              }}
              onClick={() => toggleModule(moduleId)}
              onMouseDown={e => e.preventDefault()}
            >
              <span className="d-flex align-items-center" style={{ minWidth: 0 }}>
                {isExpanded ? (
                  <ChevronDown className="text-muted mr-2 flex-shrink-0" size={16} />
                ) : (
                  <ChevronRight className="text-muted mr-2 flex-shrink-0" size={16} />
                )}
                <span className="text-truncate">{module.title}</span>
              </span>
              <span className="small text-muted flex-shrink-0 ml-1">
                {module.lessons.filter(l => completedLessons.includes(String(l._id ?? l.id ?? ''))).length} /{' '}
                {module.lessons.length}
              </span>
            </button>
          )}
        </div>
        {isExpanded || editMode ? (
          <div style={isExpanded ? undefined : { height: 0, overflow: 'hidden' }}>
            {editMode ? (
              <Droppable droppableId={`lessons-${moduleId}`} type="LESSON">
                {(lesProvided, lesSnapshot) => (
                  <div ref={lesProvided.innerRef} {...lesProvided.droppableProps}>
                    {module.lessons.map((lesson, lessonIndex) => {
                      const lessonId = String(lesson._id ?? lesson.id ?? '');
                      return (
                        <Draggable
                          key={lessonId}
                          draggableId={`lesson-${lessonId}`}
                          index={lessonIndex}
                          type="LESSON"
                          isDragDisabled={structureLocked}
                        >
                          {(dragProvided, dragSnapshot) =>
                            renderLessonRow(module, lesson, lessonIndex, dragProvided, dragSnapshot)
                          }
                        </Draggable>
                      );
                    })}
                    {lesProvided.placeholder}
                    <div
                      className="pl-5 pr-3 pb-2"
                      style={{
                        minHeight: lesSnapshot.isDraggingOver ? 28 : undefined,
                        backgroundColor: lesSnapshot.isDraggingOver ? 'rgba(13, 110, 253, 0.06)' : undefined
                      }}
                    >
                      {(() => {
                        const busyHere = addingLessonModuleId === moduleId;
                        return (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary small"
                            disabled={structureLocked}
                            onClick={() => addLesson(moduleId)}
                          >
                            {busyHere ? (
                              <>
                                <Spinner animation="border" size="sm" className="mr-1" />
                                Adding…
                              </>
                            ) : (
                              <>
                                <Plus className="mr-1" size={14} />
                                Add lesson
                              </>
                            )}
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </Droppable>
            ) : (
              <div>
                {module.lessons.map(lesson => {
                  const lessonId = String(lesson._id ?? lesson.id ?? '');
                  const isCurrent =
                    currentLesson && String(currentLesson?._id ?? currentLesson?.id ?? '') === lessonId;
                  return (
                    <div key={lessonId} className="d-flex align-items-stretch pr-2">
                      <button
                        type="button"
                        className={isCurrent ? 'text-primary' : ''}
                        style={{
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          width: '100%',
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 8px 10px 2.75rem',
                          textAlign: 'left',
                          fontSize: '0.875rem',
                          lineHeight: 1.35,
                          border: 'none',
                          borderLeft: '3px solid',
                          borderRadius: 0,
                          cursor: 'pointer',
                          borderLeftColor: isCurrent ? 'currentColor' : 'transparent',
                          backgroundColor: isCurrent ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                          fontWeight: isCurrent ? 600 : 400,
                          color: isCurrent ? undefined : '#6c757d'
                        }}
                        onClick={event => selectLessonFromList(lessonId, event)}
                        onTouchEnd={event => selectLessonFromList(lessonId, event)}
                        onMouseDown={e => e.preventDefault()}
                      >
                        {completedLessons.includes(lessonId) ? (
                          <CheckCircle className="text-success flex-shrink-0 mr-2" size={16} />
                        ) : (
                          <Circle className="text-muted flex-shrink-0 mr-2" size={16} />
                        )}
                        <span className="text-truncate">{lesson.title || 'Untitled lesson'}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const moduleList = (
    <>
      {modules.map((module, moduleIndex) => {
        const moduleId = String(module._id ?? module.id ?? '');
        if (editMode) {
          return (
            <Draggable
              key={moduleId}
              draggableId={`module-${moduleId}`}
              index={moduleIndex}
              type="MODULE"
              isDragDisabled={structureLocked || modules.length < 2}
            >
              {(modProvided, modSnapshot) => renderModuleBlock(module, moduleIndex, modProvided, modSnapshot)}
            </Draggable>
          );
        }
        return renderModuleBlock(
          module,
          moduleIndex,
          { innerRef: null, draggableProps: {}, dragHandleProps: {} },
          { isDragging: false }
        );
      })}
    </>
  );

  return (
    <aside
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: '#fff',
        borderRight: '1px solid #dee2e6',
        position: 'relative',
        zIndex: mobileLearner && outlineOpen ? 5 : undefined,
        width: isLargeViewport ? 320 : '100%',
        flexShrink: 0,
        height: isLargeViewport ? '100%' : undefined,
        maxHeight: isLargeViewport ? 'none' : undefined,
        overflow: isLargeViewport || !mobileLearner ? 'hidden' : 'visible'
      }}
    >
      <div
        style={
          editMode
            ? {
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem',
                minHeight: '2.875rem',
                padding: '0.5rem 0.75rem',
                borderBottom: '1px solid #dee2e6',
                background: '#fff',
                boxSizing: 'border-box'
              }
            : {
                flex: '0 0 auto',
                padding: '0.625rem 0.875rem',
                borderBottom: '1px solid #dee2e6',
                background: '#fff'
              }
        }
      >
        <h5 className="text-truncate mb-0" style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.3 }}>
          {mobileLearner && currentLesson
            ? `${displayCourseTitle}: ${currentLesson.title || 'Untitled lesson'}`
            : displayCourseTitle}
        </h5>
        {!editMode ? (
          mobileLearner ? null : (
            <>
              <div className="d-flex justify-content-between small text-muted mt-2 mb-1">
                <span>
                  {completedCount} of {totalLessons} lessons completed
                </span>
                <span>{progressPercentage}%</span>
              </div>
              <ProgressBar variant="primary" now={progressPercentage} style={{ height: 8, borderRadius: '1.2rem' }} />
            </>
          )
        ) : (
          <div className="w-100 small text-muted">Drag modules or lessons to reorder.</div>
        )}
        {!isLargeViewport && editMode ? (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onExitEditor}
            className="d-inline-flex align-items-center mt-2"
          >
            <ArrowLeft className="mr-1" size={14} />
            Back to courses
          </Button>
        ) : null}
      </div>
      {mobileLearner ? (
        <div
          className="d-flex align-items-center bg-white"
          style={{ flex: '0 0 auto', gap: '0.55rem', padding: '0.55rem 0.75rem 0.65rem', borderBottom: '1px solid #e9ecef' }}
        >
          <button
            type="button"
            aria-label="Back to courses"
            onClick={onExitEditor}
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0 text-dark"
            style={{
              width: 28,
              height: 36,
              border: 'none',
              background: 'transparent',
              padding: 0
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div className="text-muted" style={{ fontSize: '0.8125rem', lineHeight: 1.2, marginBottom: 5 }}>
              {completedCount} of {totalLessons} completed
            </div>
            <ProgressBar
              variant="primary"
              now={progressPercentage}
              style={{ height: 4, borderRadius: 999, backgroundColor: '#e9ecef' }}
            />
          </div>
          <span className="text-muted flex-shrink-0" style={{ fontSize: '0.875rem', lineHeight: 1 }}>
            {progressPercentage}%
          </span>
          <button
            type="button"
            aria-label={outlineOpen ? 'Close course content' : 'Show course content'}
            aria-expanded={outlineOpen}
            onClick={() => setOutlineOpen(open => !open)}
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0 text-dark"
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: '1px solid #e6e8eb',
              background: '#f4f5f7',
              padding: 0,
              touchAction: 'manipulation'
            }}
          >
            {outlineOpen ? <X size={16} /> : <List size={16} />}
          </button>
        </div>
      ) : null}
      {!isLargeViewport && !mobileLearner ? (
        <button
          type="button"
          className="d-flex align-items-center justify-content-between w-100 border-0 bg-white"
          style={{ flex: '0 0 auto', padding: '0.5rem 0.875rem', gap: '0.5rem' }}
          aria-expanded={outlineOpen}
          onClick={() => setOutlineOpen(open => !open)}
        >
          <span className="d-flex align-items-center" style={{ gap: 6 }}>
            {outlineOpen ? (
              <ChevronDown className="text-muted flex-shrink-0" size={16} />
            ) : (
              <ChevronRight className="text-muted flex-shrink-0" size={16} />
            )}
            <h6 className="text-uppercase text-muted small font-weight-bold mb-0">Course content</h6>
          </span>
          {reorderingStructure ? (
            <span className="small text-muted d-flex align-items-center">
              <Spinner animation="border" size="sm" className="mr-1" />
              Saving…
            </span>
          ) : null}
        </button>
      ) : isLargeViewport || (outlineOpen && !mobileLearner) ? (
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ flex: '0 0 auto', padding: '0.375rem 0.875rem 0.25rem' }}
        >
          <h6 className="text-uppercase text-muted small font-weight-bold mb-0">Course content</h6>
          {reorderingStructure ? (
            <span className="small text-muted d-flex align-items-center">
              <Spinner animation="border" size="sm" className="mr-1" />
              Saving…
            </span>
          ) : null}
        </div>
      ) : null}
      {mobileLearner && outlineOpen ? (
        <div
          className="course-player-scroll bg-white"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 2,
            maxHeight: 'min(60vh, calc(100svh - 220px))',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderBottom: '1px solid #dee2e6',
            boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)'
          }}
        >
          <div style={{ padding: '0.5rem 0.875rem 0.25rem' }}>
            <h6 className="text-uppercase text-muted small font-weight-bold mb-0">Course content</h6>
          </div>
          {moduleList}
        </div>
      ) : null}
      {showInlineOutline ? (
      <div
        className="course-player-scroll"
        style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
      >
        {editMode ? (
          <DragDropContext onDragEnd={onStructureDragEnd}>
            <Droppable droppableId="modules" type="MODULE">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    backgroundColor: snapshot.isDraggingOver ? 'rgba(13, 110, 253, 0.04)' : undefined
                  }}
                >
                  {moduleList}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          moduleList
        )}
      </div>
      ) : null}
      {editMode ? (
        <div className="flex-shrink-0 px-3 py-2 border-top bg-white">
          <Button variant="outline-primary" size="sm" block disabled={structureLocked} onClick={addModule}>
            {addingModule ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Adding…
              </>
            ) : (
              <>
                <Plus className="mr-1" size={14} />
                Add module
              </>
            )}
          </Button>
        </div>
      ) : null}
    </aside>
  );
}

export default CourseTrainingEditorSidebar;
